const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? ''

export type AuthUser = {
  id: number
  telegram_id: number
  username: string
  first_name: string
  balance: number
}

const TOKEN_KEY = 'tg_game_hilo_jwt'
let memoryToken: string | null | undefined

/** Read JWT from lobby handoff (?token=), sessionStorage, or memory. */
export function takeTokenFromUrl(): string | null {
  try {
    const u = new URL(window.location.href)
    const fromQuery = u.searchParams.get('token')
    if (fromQuery) {
      memoryToken = fromQuery
      try {
        sessionStorage.setItem(TOKEN_KEY, fromQuery)
      } catch {
        /* private mode */
      }
      u.searchParams.delete('token')
      const qs = u.searchParams.toString()
      window.history.replaceState(
        null,
        '',
        u.pathname + (qs ? `?${qs}` : '') + u.hash,
      )
      return fromQuery
    }
  } catch {
    /* ignore */
  }
  if (memoryToken) return memoryToken
  try {
    const stored = sessionStorage.getItem(TOKEN_KEY)
    if (stored) {
      memoryToken = stored
      return stored
    }
  } catch {
    /* ignore */
  }
  return null
}

/** Decode user_id from JWT payload (no signature verify; gateway already issued it). */
export function userIdFromToken(token: string): number {
  try {
    const part = token.split('.')[1]
    if (!part) return 0
    const b64 = part.replace(/-/g, '+').replace(/_/g, '/')
    const pad = b64 + '='.repeat((4 - (b64.length % 4)) % 4)
    const json = atob(pad)
    const claims = JSON.parse(json) as { user_id?: number }
    const id = Number(claims.user_id)
    return Number.isFinite(id) ? id : 0
  } catch {
    return 0
  }
}

export type DealResponse = {
  base_card: number
  base_suit: string
  odds_higher: number
  odds_lower: number
}

export type PlayRound = {
  id: string
  user_id: number
  base_card: number
  base_suit: string
  next_card: number
  next_suit: string
  choice: string
  odds: number
  bet_amount: number
  payout: number
  won: boolean
}

async function request<T>(
  path: string,
  options: RequestInit & { token?: string } = {},
): Promise<T> {
  const headers = new Headers(options.headers)
  if (options.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json')
  }
  if (options.token) {
    headers.set('Authorization', `Bearer ${options.token}`)
  }
  const res = await fetch(`${API_BASE}${path}`, { ...options, headers })
  if (!res.ok) {
    let msg = res.statusText
    try {
      const body = (await res.json()) as { error?: string }
      if (body.error) msg = body.error
    } catch {
      /* ignore */
    }
    throw new Error(msg)
  }
  return (await res.json()) as T
}

export async function loginWithInitData(initData: string) {
  if (import.meta.env.VITE_DEMO_MODE === 'true') {
    const { loginDemo } = await import('../../../shared/demo/adapters/auth.ts')
    return loginDemo()
  }
  return request<{ token: string; user: AuthUser }>('/api/auth/telegram', {
    method: 'POST',
    body: JSON.stringify({ init_data: initData }),
  })
}

/** Local play without Telegram (Gateway debug mode: POST /api/auth/dev). */
export async function loginDev(
  body: { username?: string; first_name?: string; telegram_id?: number } = {},
) {
  if (import.meta.env.VITE_DEMO_MODE === 'true') {
    const { loginDemo } = await import('../../../shared/demo/adapters/auth.ts')
    return loginDemo()
  }
  return request<{ token: string; user: AuthUser }>('/api/auth/dev', {
    method: 'POST',
    body: JSON.stringify(body),
  })
}

export async function fetchBalance(token: string) {
  if (import.meta.env.VITE_DEMO_MODE === 'true') {
    const { fetchBalance: demoBalance } = await import('../../../shared/demo/adapters/auth.ts')
    return demoBalance()
  }
  return request<{ balance: number }>('/api/wallet/balance', { token })
}

export async function deal(token: string) {
  if (import.meta.env.VITE_DEMO_MODE === 'true') {
    const { dealHilo } = await import('../../../shared/demo/adapters/hilo.ts')
    return dealHilo()
  }
  return request<DealResponse>('/api/games/hilo/deal', {
    method: 'POST',
    token,
  })
}

export async function play(
  token: string,
  body: {
    base_card: number
    base_suit: string
    bet_amount: number
    choice: 'higher' | 'lower'
  },
) {
  if (import.meta.env.VITE_DEMO_MODE === 'true') {
    const { playHilo } = await import('../../../shared/demo/adapters/hilo.ts')
    return playHilo(body)
  }
  return request<{ round: PlayRound }>('/api/games/hilo/play', {
    method: 'POST',
    token,
    body: JSON.stringify(body),
  })
}

export async function getInitData(): Promise<string> {
  const dev = (import.meta.env.VITE_DEV_INIT_DATA as string | undefined) ?? ''
  try {
    const legacy = (
      window as unknown as {
        Telegram?: { WebApp?: { initData?: string; ready?: () => void } }
      }
    ).Telegram?.WebApp
    legacy?.ready?.()
    if (legacy?.initData) return legacy.initData
  } catch {
    /* ignore */
  }
  try {
    const raw = await Promise.race([
      (async () => {
        const { init, isTMA, retrieveRawInitData } = await import(
          '@telegram-apps/sdk-react'
        )
        if (await isTMA('complete')) {
          init()
          return retrieveRawInitData() || ''
        }
        return ''
      })(),
      new Promise<string>((resolve) => {
        window.setTimeout(() => resolve(''), 2000)
      }),
    ])
    if (raw) return raw
  } catch {
    /* ignore */
  }
  return dev
}

export function rankLabel(rank: number): string {
  if (rank === 1) return 'A'
  if (rank === 11) return 'J'
  if (rank === 12) return 'Q'
  if (rank === 13) return 'K'
  return String(rank)
}

export function suitSymbol(suit: string): string {
  switch (suit) {
    case 'hearts':
      return '♥'
    case 'diamonds':
      return '♦'
    case 'clubs':
      return '♣'
    case 'spades':
      return '♠'
    default:
      return '?'
  }
}

export function isRedSuit(suit: string): boolean {
  return suit === 'hearts' || suit === 'diamonds'
}
